import useAuthStore from '../store/authStore';
import { useQuranCenter, useCaffeCenter, useSportCenter, useLearningCenter } from '../hooks/useStudentLabel';
import { planFeaturesOf, WHATSAPP_PATHS } from '../hooks/usePlanFeatures';

/**
 * The app's navigation model — one list, two surfaces.
 *
 * The desktop Sidebar and the mobile SideDrawer used to each hard-code their
 * own copy of this list, and the copies drifted: the drawer was missing
 * Timetable, Registration, Teacher surveys, Birthday gifts, WhatsApp templates
 * and bulk send, Onboarding and Tournaments — so a phone user simply could not
 * reach those pages. Both surfaces now read from here, which is the only way
 * "the menu shows every page" stays true after the next page is added.
 *
 * Item shape:
 *   labelKey       i18n key for the label
 *   labelFallback  English default when the key is missing (optional)
 *   iconName       Material icon name
 *   path           route to navigate to
 *   mobile: false  desktop-only — hidden from the mobile drawer
 *
 * Nothing is currently desktop-only: course registration was, until the menu
 * shipped without it and the gap was the first thing reported. The flag stays
 * because the mechanism is worth keeping, but the bar for using it is that the
 * page genuinely cannot work on a phone.
 */

const SETTINGS = { labelKey: 'sidebar.settings', iconName: 'settings', path: '/settings' };

const LEARNING_ITEMS = [
  { labelKey: 'sidebar.dashboard', iconName: 'dashboard', path: '/dashboard' },
  { labelKey: 'sidebar.lessons', iconName: 'menu_book', path: '/lessons' },
  { labelKey: 'sidebar.students', iconName: 'person', path: '/students' },
  { labelKey: 'sidebar.teachers', iconName: 'group', path: '/teachers' },
  // Payments the center paid its teachers (pages/TeacherPayoutsPage.jsx).
  { labelKey: 'teacherPayouts.navLabel', iconName: 'price_check', path: '/teachers/payments' },
  // Each teacher's lessons + hours per month, for salaries (شروق 04/11).
  { labelKey: 'teacherHours.navLabel', iconName: 'schedule', path: '/teachers/hours' },
  { labelKey: 'sidebar.groups', iconName: 'class', path: '/groups' },
  { labelKey: 'courseRegistration.sidebar', iconName: 'how_to_reg', path: '/registration' },
  { labelKey: 'sidebar.calendar', iconName: 'event', path: '/calendar' },
  { labelKey: 'sidebar.timetable', iconName: 'grid_on', path: '/timetable' },
  { labelKey: 'sidebar.jibaya', iconName: 'money', path: '/jibaya' },
  // Payments board (لوحة الدفعات) — the sport board's generic learning variant:
  // who owes / is overdue, bulk payment + private-lesson price.
  { labelKey: 'learningBoard.navLabel', iconName: 'account_balance_wallet', path: '/payments' },
  { labelKey: 'sidebar.analytics', iconName: 'analytics', path: '/analytics' },
  // Campaign leads. These pages already existed but were reachable only by
  // hair_transplant centres, so a learning centre running click-to-WhatsApp
  // ads had no tab and got a 403 even typing /automation/leads directly.
  { labelKey: 'sidebar.metaLeads', iconName: 'campaign', path: '/automation/meta-leads' },
  { labelKey: 'sidebar.metaLeadsList', iconName: 'inbox', path: '/automation/leads' },
  { labelKey: 'survey.builderTitle', iconName: 'construction', path: '/surveys' },
  { labelKey: 'teacherSurveys.sidebar', iconName: 'rate_review', path: '/teacher-surveys' },
  { labelKey: 'sidebar.birthdayGifts', iconName: 'cake', path: '/birthday-gifts' },
  { labelKey: 'sidebar.reports', iconName: 'summarize', labelFallback: 'Reports', path: '/reports' },
  { labelKey: 'sidebar.auditLog', iconName: 'history', labelFallback: 'Audit Log', path: '/audit-log' },
  { labelKey: 'sidebar.whatsappTemplates', iconName: 'chat', path: '/settings/whatsapp-templates' },
  { labelKey: 'bulkWhatsApp.sidebarLabel', iconName: 'campaign', path: '/whatsapp/bulk-send' },
  SETTINGS,
];

// Sport sub-type (learning + learningSubType 'sport'). Labels come from the
// same keys as learning; useStudentLabel/i18n render them as Sessions /
// Clients / Billing for sport.
const SPORT_ITEMS = [
  { labelKey: 'sidebar.dashboard', iconName: 'dashboard', path: '/dashboard' },
  { labelKey: 'sidebar.lessons', iconName: 'event', path: '/lessons' },
  { labelKey: 'sidebar.students', iconName: 'person', path: '/students' },
  { labelKey: 'sidebar.groups', iconName: 'groups', path: '/groups' },
  // Coaches: without it a sport center had no way to add the trainers that
  // groups, sessions and coach pay all depend on.
  { labelKey: 'sidebar.teachers', iconName: 'sports', path: '/teachers' },
  { labelKey: 'teacherPayouts.navLabel', iconName: 'price_check', path: '/teachers/payments' },
  // Client balances — replaces the paper الدفعات list (GAP-ANALYSIS P0-5).
  { labelKey: 'sport.balances.navLabel', iconName: 'account_balance_wallet', path: '/sport/balances' },
  { labelKey: 'sport.payroll.navLabel', iconName: 'payments', path: '/sport/payroll' },
  { labelKey: 'courseRegistration.sidebar', iconName: 'how_to_reg', path: '/registration' },
  { labelKey: 'sidebar.jibaya', iconName: 'receipt_long', path: '/jibaya' },
  // Birthday greetings for the (mostly kid) clients — pages/BirthdaysPage.jsx.
  { labelKey: 'sidebar.birthdays', iconName: 'cake', path: '/birthdays' },
  SETTINGS,
];

const THERAPY_ITEMS = [
  { labelKey: 'sidebar.dashboard', iconName: 'dashboard', path: '/dashboard' },
  { labelKey: 'sidebar.students', iconName: 'person', path: '/students' },
  { labelKey: 'sidebar.calendar', iconName: 'event', path: '/calendar' },
  { labelKey: 'survey.builderTitle', iconName: 'construction', path: '/surveys' },
  { labelKey: 'sidebar.therapyAnalytics', iconName: 'analytics', labelFallback: 'Therapy Analytics', path: '/therapy-analytics' },
  { labelKey: 'staffAttendance.nav.attendance', iconName: 'schedule', path: '/staff-attendance' },
  { labelKey: 'sidebar.birthdayGifts', iconName: 'cake', path: '/birthday-gifts' },
  { labelKey: 'sidebar.reports', iconName: 'summarize', labelFallback: 'Reports', path: '/reports' },
  { labelKey: 'sidebar.auditLog', iconName: 'history', labelFallback: 'Audit Log', path: '/audit-log' },
  { labelKey: 'sidebar.whatsappTemplates', iconName: 'chat', path: '/settings/whatsapp-templates' },
  { labelKey: 'bulkWhatsApp.sidebarLabel', iconName: 'campaign', path: '/whatsapp/bulk-send' },
  SETTINGS,
];

const HAIR_TRANSPLANT_ITEMS = [
  { labelKey: 'sidebar.dashboard', iconName: 'dashboard', path: '/dashboard' },
  { labelKey: 'sidebar.treatments', iconName: 'vaccines', labelFallback: 'Treatments', path: '/treatments' },
  { labelKey: 'followUps.title', iconName: 'phone_in_talk', path: '/followups' },
  // Single "Clients" tab — the old /patients tab showed the same
  // /transplant-clients data under a different name and has been merged in.
  { labelKey: 'sidebar.students', iconName: 'person', path: '/students' },
  { labelKey: 'sidebar.clinicDocuments', iconName: 'folder_open', path: '/clinic-documents' },
  { labelKey: 'sidebar.calendar', iconName: 'event', path: '/calendar' },
  { labelKey: 'sidebar.workers', iconName: 'badge', labelFallback: 'Workers', path: '/workers' },
  { labelKey: 'staffAttendance.nav.attendance', iconName: 'schedule', path: '/staff-attendance' },
  { labelKey: 'sidebar.inventory', iconName: 'inventory_2', labelFallback: 'Inventory', path: '/inventory' },
  { labelKey: 'sidebar.automation', iconName: 'notifications_active', labelFallback: 'Automation', path: '/automation' },
  { labelKey: 'sidebar.metaLeads', iconName: 'campaign', path: '/automation/meta-leads' },
  { labelKey: 'sidebar.metaLeadsList', iconName: 'inbox', path: '/automation/leads' },
  { labelKey: 'sidebar.clinicAnalytics', iconName: 'analytics', labelFallback: 'Analytics', path: '/clinic-analytics' },
  { labelKey: 'survey.builderTitle', iconName: 'construction', path: '/surveys' },
  { labelKey: 'sidebar.birthdayGifts', iconName: 'cake', path: '/birthday-gifts' },
  { labelKey: 'sidebar.reports', iconName: 'summarize', labelFallback: 'Reports', path: '/reports' },
  { labelKey: 'sidebar.auditLog', iconName: 'history', labelFallback: 'Audit Log', path: '/audit-log' },
  { labelKey: 'sidebar.whatsappTemplates', iconName: 'chat', path: '/settings/whatsapp-templates' },
  { labelKey: 'bulkWhatsApp.sidebarLabel', iconName: 'campaign', path: '/whatsapp/bulk-send' },
  SETTINGS,
];

// Pharma clinic: only Clients and Orders, plus Settings. Orders arrive from the
// public marketing site; Clients reuses the clinic-family /students data.
const PHARMA_CLINIC_ITEMS = [
  { labelKey: 'sidebar.students', iconName: 'person', path: '/students' },
  { labelKey: 'orders.title', iconName: 'receipt_long', labelFallback: 'Orders', path: '/orders' },
  SETTINGS,
];

// Teachers get reporting features only, plus the read-only weekly timetable.
const TEACHER_ITEMS = [
  { labelKey: 'sidebar.timetable', iconName: 'grid_on', path: '/timetable' },
  { labelKey: 'groups.checklists', iconName: 'checklist', path: '/teacher/checklists' },
  { labelKey: 'groups.attendance', iconName: 'event_note', path: '/teacher/attendance' },
  { labelKey: 'teacherSurveys.sidebar', iconName: 'rate_review', path: '/teacher-surveys' },
];

// Workers in a hair_transplant center (doctors, technicians, …).
const WORKER_ITEMS = [
  { labelKey: 'sidebar.workerDashboard', iconName: 'dashboard', path: '/worker/dashboard' },
  { labelKey: 'sidebar.calendar', iconName: 'event', path: '/calendar' },
  { labelKey: 'bulkWhatsApp.sidebarLabel', iconName: 'campaign', path: '/whatsapp/bulk-send' },
];

/** Insert `item` right after the entry at `afterPath`, else append. */
function insertAfter(items, afterPath, item) {
  const idx = items.findIndex((m) => m.path === afterPath);
  const next = [...items];
  next.splice(idx >= 0 ? idx + 1 : next.length, 0, item);
  return next;
}

/** Insert `item` just before the entry at `beforePath`, else append. */
function insertBefore(items, beforePath, item) {
  const idx = items.findIndex((m) => m.path === beforePath);
  const next = [...items];
  next.splice(idx >= 0 ? idx : next.length, 0, item);
  return next;
}

/**
 * The navigation items this user should see.
 *
 * @param {'desktop'|'mobile'} surface  'mobile' drops desktop-only entries.
 */
export default function useNavMenuItems(surface = 'desktop') {
  const user = useAuthStore((s) => s.user);
  const centerType = useAuthStore((s) => s.centerType);
  const { isQuran } = useQuranCenter();
  const { isCaffe } = useCaffeCenter();
  const { isSport } = useSportCenter();
  const { isLearning: isLearningKind } = useLearningCenter();

  const isLearning = !['therapy', 'hair_transplant', 'pharma_clinic'].includes(centerType);

  let items;
  if (user?.role === 'teacher') {
    items = centerType === 'hair_transplant' ? WORKER_ITEMS : TEACHER_ITEMS;
  } else if (centerType === 'therapy') {
    items = THERAPY_ITEMS;
  } else if (centerType === 'hair_transplant') {
    items = HAIR_TRANSPLANT_ITEMS;
  } else if (centerType === 'pharma_clinic') {
    items = PHARMA_CLINIC_ITEMS;
  } else {
    items = LEARNING_ITEMS;
  }

  const isLearningAdmin = isLearning && user?.role !== 'teacher';

  // Quran (Hifz) centers get the teacher-onboarding review queue after Groups.
  if (isLearningAdmin && isQuran) {
    items = insertAfter(items, '/groups', {
      labelKey: 'onboarding.queueTitle', iconName: 'inbox', path: '/onboarding',
    });
  }

  // Caffe centers get Tournaments after Groups.
  if (isLearningAdmin && isCaffe) {
    items = insertAfter(items, '/groups', {
      labelKey: 'tournaments.title', iconName: 'emoji_events', path: '/tournaments',
    });
  }

  // The payments board is for learning centers (utils/centerKind.js /
  // useLearningCenter): a caffe center (tournaments) does not get it.
  if (!isLearningKind) {
    items = items.filter((i) => i.path !== '/payments');
  }

  // Sport centers run a deliberately short menu — only what a gym/academy
  // manager uses day to day. The other learning pages still exist and their
  // routes still work; they are just not advertised here.
  if (isLearningAdmin && isSport) {
    items = SPORT_ITEMS;
  }

  // WhatsApp inbox — replies to the center's official number, every center
  // type, admins only (teachers/workers have no reply rights).
  if (user?.role !== 'teacher') {
    items = insertBefore(items, '/settings', {
      labelKey: 'whatsappInbox.navLabel', iconName: 'chat', path: '/whatsapp/inbox',
    });
  }

  // Cross-center leads, for super-admins on any center type. The page spans
  // every center rather than belonging to one, so it sits with the admin-ish
  // entries just before Settings.
  if (user?.role === 'super_admin') {
    items = insertBefore(items, '/settings', {
      labelKey: 'sidebar.allCentersLeads', iconName: 'inbox', path: '/automation/all-leads',
    });
  }

  // Basic package: no WhatsApp pages (the backend refuses the sends too).
  if (!planFeaturesOf(user).whatsapp) {
    items = items.filter((i) => !WHATSAPP_PATHS.includes(i.path));
  }

  return surface === 'mobile' ? items.filter((i) => i.mobile !== false) : items;
}

export { LEARNING_ITEMS, SPORT_ITEMS, THERAPY_ITEMS, HAIR_TRANSPLANT_ITEMS, PHARMA_CLINIC_ITEMS, TEACHER_ITEMS, WORKER_ITEMS };
